/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, memo, useEffect } from 'react';
import DataIntegrationContext from './DataIntegrationContext';
import initialDataIntegrationState from './initialDataIntegrationState';
import dataIntegrationReducer from 'contexts/dataIntegration/reducer/dataIntegration.reducer';
import * as action from 'contexts/dataIntegration/reducer/dataIntegration.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { ChildrenProps } from 'interfaces/general.interface';
import { useTranslation } from 'react-i18next';
import {
  IcreateAudienceAPIBody,
  IcreateFilePatternAPIBody,
  IcreateMappingAPIBody,
  IcreateMappingFieldAPIBodyItem,
  IcreatePointsOfSaleDictionaryAPIBodyItem,
  IgetAudiencesAPIResponseItem,
  IgetHistoryAPIResponseItem,
  IgetMappingFieldsAPIResponseItem,
  IgetMappingsAPIResponseItem,
  IgetPointsOfSaleDictionaryAPIResponseItem,
  IupdateAudienceAPIBody,
  IupdateMappingAPIBody,
  IupdateMappingFieldAPIBody,
  IupdatePointsOfSaleDictionaryAPIBody,
  IuploadFilesAPIBodyItem,
} from 'api/endpoints/dataIntegration/dataIntegration.response.interface';
import * as types from './reducer/dataIntegration.types';
import useContextCompany from 'hooks/contexts/useContextCompany';
import {
  IgetImportFilesHistoryProps,
  IupdateInboxFileEncodingProps,
  TsetCSVSelectedProps,
} from 'interfaces/dataIntegration.interface';
import useContextGeneral from 'hooks/contexts/useContextGeneral';

function DataIntegrationProvider(props: ChildrenProps) {
  const [dataIntegrationState, dispatch] = useReducer(
    dataIntegrationReducer,
    initialDataIntegrationState
  );
  const { getAccessTokenSilently } = useAuth0();
  const { t: translate } = useTranslation();
  const { company } = useContextCompany();
  const { ping } = useContextGeneral();

  // History /////////////////////////////////////////////////////
  const getImportFilesHistory = useCallback(
    async ({ audience, state, search, runDataPipeline }: IgetImportFilesHistoryProps) => {
      const token = await getAccessTokenSilently();
      if (!token || !company?.id) return false;
      return await action.getImportFilesHistoryAction({
        dispatch,
        importFilesHistoryBody: dataIntegrationState.importFilesHistoryBody,
        companyId: company.id,
        token,
        translate,
        runDataPipeline,
        audience,
        state,
        search,
      });
    },
    [company?.id, dataIntegrationState.importFilesHistoryBody]
  );

  const handlePagination = useCallback(
    async ({ page, pageSize }: { page: number; pageSize: number }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.handlePaginationAction({
        dispatch,
        page,
        pageSize,
        dataIntegrationState,
        token,
        translate,
      });
    },
    [dataIntegrationState]
  );

  const selectImportFilesHistory = useCallback(
    (importFilesHistory: IgetHistoryAPIResponseItem | null) => {
      dispatch({
        type: types.SET_SELECTED_IMPORT_FILES_HISTORY,
        payload: importFilesHistory,
      });
    },
    [dispatch]
  );

  // Audiences /////////////////////////////////////////////////////
  useEffect(() => {
    if (!ping) return;
    (async () => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.getAudiencesAction({
        dispatch,
        token,
        translate,
      });
    })();
  }, [ping]);

  const getAudiences = useCallback(async () => {
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.getAudiencesAction({
      dispatch,
      token,
      translate,
    });
  }, []);

  const selectAudience = useCallback(
    (audience: IgetAudiencesAPIResponseItem | null) => {
      dispatch({ type: types.SET_AUDIENCE_SELECTED, payload: audience });
    },
    [dispatch]
  );

  const createAudience = useCallback(
    async ({ body }: { body: IcreateAudienceAPIBody }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.createAudienceAction({
        dispatch,
        token,
        body,
        translate,
      });
    },
    []
  );

  const updateAudience = useCallback(
    async ({ body }: { body: IupdateAudienceAPIBody }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updateAudienceAction({
        dispatch,
        token,
        body,
        translate,
      });
    },
    []
  );

  // Mapping fields /////////////////////////////////////////////////////
  useEffect(() => {
    (async () => {
      if (!ping) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.getMappingFieldsAction({
        dispatch,
        token,
        audience: null,
        translate,
      });
    })();
  }, [ping]);

  const getMappingFields = useCallback(
    async ({ audience }: { audience: string | null }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.getMappingFieldsAction({
        dispatch,
        token,
        audience,
        translate,
      });
    },
    []
  );

  const selectMappingField = useCallback(
    (mappingField: IgetMappingFieldsAPIResponseItem | null) => {
      dispatch({ type: types.SET_MAPPING_FIELD_SELECTED, payload: mappingField });
    },
    [dispatch]
  );

  const createMappingField = useCallback(
    async ({ body }: { body: IcreateMappingFieldAPIBodyItem }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.createMappingFieldAction({
        dispatch,
        token,
        body,
        mappingFieldsAudienceSelected: dataIntegrationState.mappingFieldsAudienceSelected,
        translate,
      });
    },
    [dataIntegrationState.mappingFieldsAudienceSelected]
  );

  const updateMappingField = useCallback(
    async ({ body }: { body: IupdateMappingFieldAPIBody }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updateMappingFieldAction({
        dispatch,
        token,
        body,
        translate,
      });
    },
    []
  );

  const deleteMappingField = useCallback(
    async ({ mappingFieldId }: { mappingFieldId: string }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.deleteMappingFieldAction({
        dispatch,
        token,
        mappingFieldId,
        translate,
      });
    },
    []
  );

  // Mappings /////////////////////////////////////////////////////
  const getMappings = useCallback(async () => {
    const token = await getAccessTokenSilently();
    if (!token || !company?.id) return false;
    return await action.getMappingsAction({
      dispatch,
      companyId: company.id,
      token,
      translate,
    });
  }, [company?.id]);

  const selectMapping = useCallback(
    (mapping: IgetMappingsAPIResponseItem | null) => {
      dispatch({ type: types.SET_MAPPING_SELECTED, payload: mapping });
    },
    [dispatch]
  );

  const createMapping = useCallback(async ({ body }: { body: IcreateMappingAPIBody }) => {
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.createMappingAction({
      dispatch,
      token,
      body,
      translate,
    });
  }, []);

  const selectCreateMapping = useCallback(
    (mapping: IcreateMappingAPIBody | null) => {
      dispatch({ type: types.SET_CREATE_MAPPING_SELECTED, payload: mapping });
    },
    [dispatch]
  );

  const updateMapping = useCallback(async ({ body }: { body: IupdateMappingAPIBody }) => {
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.updateMappingAction({
      dispatch,
      token,
      body,
      translate,
    });
  }, []);

  const deleteMap = useCallback(async ({ mapId }: { mapId: string }) => {
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.deleteMapAction({
      dispatch,
      token,
      mapId,
      translate,
    });
  }, []);

  const setCSVSelected = useCallback(async ({ csvData, file }: TsetCSVSelectedProps) => {
    return await action.setCSVSelectedAction({
      dispatch,
      csvData,
      file,
    });
  }, []);

  const setIsLoadingCSVSelected = useCallback((isLoading: boolean) => {
    dispatch({
      type: types.SET_IS_LOADING_CSV_SELECTED,
      payload: isLoading,
    });
  }, []);

  const setRecordToUpdate = useCallback((record: string | null) => {
    dispatch({
      type: types.SET_RECORD_TO_UPDATE,
      payload: record,
    });
  }, []);

  // Points of sale dictionaries /////////////////////////////////////////////////////
  const getPointsOfSaleDictionary = useCallback(async () => {
    const token = await getAccessTokenSilently();
    if (!token || !company?.id) return false;
    return await action.getPointsOfSaleDictionaryAction({
      dispatch,
      companyId: company.id,
      token,
      translate,
    });
  }, [company?.id]);

  const selectPointOfSaleDictionary = useCallback(
    (pointOfSaleDictionary: IgetPointsOfSaleDictionaryAPIResponseItem | null) => {
      dispatch({
        type: types.SET_POINT_OF_SALE_DICTIONARY_SELECTED,
        payload: pointOfSaleDictionary,
      });
    },
    [dispatch]
  );

  const createPointsOfSaleDictionary = useCallback(
    async ({ body }: { body: IcreatePointsOfSaleDictionaryAPIBodyItem }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.createPointsOfSaleDictionaryAction({
        dispatch,
        token,
        body,
        translate,
      });
    },
    []
  );

  const updatePointsOfSaleDictionary = useCallback(
    async ({ body }: { body: IupdatePointsOfSaleDictionaryAPIBody }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updatePointsOfSaleDictionaryAction({
        dispatch,
        token,
        body,
        translate,
      });
    },
    []
  );

  const deletePointsOfSaleDictionary = useCallback(
    async ({ pointOfSaleDictionaryId }: { pointOfSaleDictionaryId: string }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.deletePointsOfSaleDictionaryAction({
        dispatch,
        token,
        pointOfSaleDictionaryId,
        translate,
      });
    },
    []
  );

  // Files Patterns /////////////////////////////////////////////////////
  const getFilePatterns = useCallback(async () => {
    const token = await getAccessTokenSilently();
    if (!token || !company?.id) return false;
    return await action.getFilePatternsAction({
      dispatch,
      companyId: company.id,
      token,
      translate,
    });
  }, [company?.id]);

  const createFilePattern = useCallback(
    async ({ body }: { body: IcreateFilePatternAPIBody }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.createFilePatternAction({
        dispatch,
        token,
        body,
        translate,
      });
    },
    []
  );

  // Upload files /////////////////////////////////////////////////////
  const uploadFiles = useCallback(
    async ({ body }: { body: Array<IuploadFilesAPIBodyItem> }) => {
      const token = await getAccessTokenSilently();
      if (!token)
        return {
          ok: false,
          confirmedItems: [],
        };
      return await action.uploadFilesAction({
        dispatch,
        token,
        body,
        translate,
      });
    },
    []
  );

  const setIsLoadingUploadFiles = useCallback((isLoading: boolean) => {
    dispatch({
      type: types.SET_IS_LOADING_UPLOAD_FILES,
      payload: isLoading,
    });
  }, []);

  const reprocessImport = useCallback(
    async ({
      importIds,
      reprocessAll,
    }: {
      importIds: string[];
      reprocessAll: boolean;
    }) => {
      const token = await getAccessTokenSilently();
      if (!token || !company?.id) return false;
      return await action.reprocessImportAction({
        dispatch,
        companyId: company.id,
        importFilesHistoryBody: dataIntegrationState.importFilesHistoryBody,
        token,
        importIds,
        translate,
        reprocessAll,
      });
    },
    [company?.id, dataIntegrationState.importFilesHistoryBody]
  );

  const updateInboxFileEncoding = useCallback(
    async ({ inboxId, fileEncoding }: IupdateInboxFileEncodingProps) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updateInboxFileEncodingAction({
        token,
        inboxId,
        fileEncoding,
        translate,
      });
    },
    []
  );

  const archiveFileHistory = useCallback(
    async ({ importHistoryIds }: { importHistoryIds: Array<string> }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.archiveFileHistoryAction({
        dispatch,
        importFilesHistoryBody: dataIntegrationState.importFilesHistoryBody,
        importHistoryIds,
        token,
        translate,
      });
    },
    [dataIntegrationState.importFilesHistoryBody]
  );

  const memoProvider = useMemo(
    () => ({
      ...dataIntegrationState,
      // History
      getImportFilesHistory,
      handlePagination,
      selectImportFilesHistory,
      // Audiences
      getAudiences,
      selectAudience,
      createAudience,
      updateAudience,
      // Mapping fields
      getMappingFields,
      selectMappingField,
      createMappingField,
      updateMappingField,
      deleteMappingField,
      // Mappings
      getMappings,
      selectMapping,
      createMapping,
      selectCreateMapping,
      updateMapping,
      deleteMap,
      setCSVSelected,
      setIsLoadingCSVSelected,
      setRecordToUpdate,
      // Points of sale dictionaries
      getPointsOfSaleDictionary,
      selectPointOfSaleDictionary,
      createPointsOfSaleDictionary,
      updatePointsOfSaleDictionary,
      deletePointsOfSaleDictionary,
      // Files Patterns
      getFilePatterns,
      createFilePattern,
      // Upload files
      uploadFiles,
      setIsLoadingUploadFiles,
      reprocessImport,
      updateInboxFileEncoding,
      archiveFileHistory,
    }),
    [
      dataIntegrationState,
      // History
      getImportFilesHistory,
      handlePagination,
      selectImportFilesHistory,
      // Audiences
      getAudiences,
      selectAudience,
      createAudience,
      updateAudience,
      // Mapping fields
      getMappingFields,
      selectMappingField,
      createMappingField,
      updateMappingField,
      deleteMappingField,
      // Mappings
      getMappings,
      selectMapping,
      createMapping,
      selectCreateMapping,
      updateMapping,
      deleteMap,
      setCSVSelected,
      setIsLoadingCSVSelected,
      setRecordToUpdate,
      // Points of sale dictionaries
      getPointsOfSaleDictionary,
      selectPointOfSaleDictionary,
      createPointsOfSaleDictionary,
      updatePointsOfSaleDictionary,
      deletePointsOfSaleDictionary,
      // Files Patterns
      getFilePatterns,
      createFilePattern,
      // Upload files
      uploadFiles,
      setIsLoadingUploadFiles,
      reprocessImport,
      updateInboxFileEncoding,
      archiveFileHistory,
    ]
  );

  return (
    <DataIntegrationContext.Provider value={memoProvider}>
      {props.children}
    </DataIntegrationContext.Provider>
  );
}

export default memo(DataIntegrationProvider);
